import { motion } from "framer-motion";
import { Clock, ExternalLink, Mail, MapPin, Phone, ShieldX } from "lucide-react";

import SectionHeader from "@/components/SectionHeader";
import { useCookieConsent } from "@/hooks/use-cookie-consent";

const openingHours = [
  { day: "Montag – Samstag", hours: "06:00 – 20:00 Uhr" },
  { day: "Sonntag", hours: "Geschlossen" },
];

const contactLines = [
  {
    icon: Phone,
    href: "tel:+4915141315272",
    label: "0151 41315272",
    detail: "Telefon",
  },
  {
    icon: Mail,
    href: "mailto:info@beton2go-romano.de",
    label: "info@beton2go-romano.de",
    detail: "E-Mail",
  },
];

const StandortSection = () => {
  const { isCategoryEnabled, openPreferences, savePreferences } = useCookieConsent();
  const hasExternalMediaConsent = isCategoryEnabled("externalMedia");

  return (
    <section id="standort" className="section-shell overflow-hidden">
      <div className="container mx-auto px-4">
        <SectionHeader
          eyebrow="Standort"
          title={
            <>
              Nah an der Baustelle.
              <br />
              Klar erreichbar.
            </>
          }
          description="Die Station ist so angelegt, dass Anfahrt, Verladung und Weiterfahrt ohne Umwege funktionieren."
          className="max-w-3xl"
        />

        <div className="grid gap-6 xl:grid-cols-[minmax(360px,420px)_minmax(0,1fr)] xl:items-stretch xl:gap-8">
          <div className="grid gap-4 xl:h-full xl:grid-rows-3">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5 }}
              className="surface-panel h-full p-6 md:p-7"
            >
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-primary" />
                <p className="font-display text-lg uppercase tracking-[0.06em] text-foreground md:text-xl">
                  Öffnungszeiten
                </p>
              </div>

              <div className="mt-6 divide-y divide-white/10">
                {openingHours.map((entry) => (
                  <div key={entry.day} className="flex items-center justify-between gap-4 py-4">
                    <span className="text-sm text-muted-foreground md:text-base">{entry.day}</span>
                    <span
                      className={`font-display text-base uppercase tracking-[0.05em] ${
                        entry.hours === "Geschlossen" ? "text-rose-300" : "text-foreground"
                      }`}
                    >
                      {entry.hours}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.a
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: 0.06 }}
              href="https://maps.google.com/?q=Primsaue+5+66809+Nalbach"
              target="_blank"
              rel="noopener noreferrer"
              className="surface-panel group flex h-full flex-col p-6 transition-colors hover:border-primary/25 md:p-7"
            >
              <div className="flex items-center gap-3">
                <MapPin className="h-5 w-5 text-primary" />
                <p className="font-display text-lg uppercase tracking-[0.06em] text-foreground md:text-xl">
                  Adresse
                </p>
              </div>

              <p className="mt-6 font-display text-3xl uppercase leading-none text-foreground">
                Primsaue 5
              </p>
              <p className="mt-3 text-base text-muted-foreground">66809 Nalbach, Deutschland</p>
              <div className="mt-auto pt-7 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-primary/85">
                Extern in Google Maps öffnen
                <ExternalLink className="h-4 w-4" />
              </div>
            </motion.a>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: 0.12 }}
              className="surface-panel h-full p-6 md:p-7"
            >
              <p className="font-display text-lg uppercase tracking-[0.06em] text-foreground md:text-xl">
                Kontakt
              </p>

              <div className="mt-6 divide-y divide-white/10">
                {contactLines.map((item) => (
                  <a
                    key={item.label}
                    href={item.href}
                    className="flex items-start gap-4 py-4 transition-colors hover:text-primary"
                  >
                    <div className="mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-primary/18 bg-primary/10">
                      <item.icon className="h-4.5 w-4.5 text-primary" />
                    </div>
                    <div>
                      <p className="text-base text-foreground md:text-lg">{item.label}</p>
                      <p className="mt-1 text-sm text-muted-foreground">{item.detail}</p>
                    </div>
                  </a>
                ))}
              </div>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.65 }}
            className="surface-panel-strong overflow-hidden p-3 md:p-4 xl:h-full"
          >
            <div className="flex h-full flex-col">
              <div className="border-b border-white/10 px-4 py-4 md:px-6">
                <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="info-label text-primary/80">Google Maps</p>
                    <p className="mt-2 font-display text-2xl uppercase leading-none text-foreground md:text-3xl">
                      Standort direkt ansehen
                    </p>
                    <p className="mt-2 text-sm text-muted-foreground md:text-base">
                      Primsaue 5, 66809 Nalbach
                    </p>
                  </div>

                  <a
                    href="https://maps.google.com/?q=Primsaue+5+66809+Nalbach"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 self-start rounded-full border border-white/12 bg-white/[0.03] px-5 py-3 font-display text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:bg-white/[0.08]"
                  >
                    Extern öffnen
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              </div>

              <div className="relative min-h-[360px] flex-1 overflow-hidden rounded-[calc(var(--radius)+2px)] md:min-h-[460px]">
                {hasExternalMediaConsent ? (
                  <iframe
                    src="https://www.google.com/maps?q=Primsaue+5,+66809+Nalbach,+Deutschland&output=embed"
                    title="Beton2Go Standort Nalbach"
                    loading="lazy"
                    allowFullScreen
                    referrerPolicy="no-referrer-when-downgrade"
                    className="absolute inset-0 block h-full w-full"
                    style={{ border: 0 }}
                  />
                ) : (
                  <div className="absolute inset-0 flex items-end bg-[linear-gradient(160deg,hsl(222_22%_12%),hsl(222_24%_8%))] p-8 md:p-10">
                    <div className="absolute inset-0 bg-[radial-gradient(circle_at_16%_18%,hsl(206_90%_58%/0.14),transparent_28%),linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:auto,64px_64px,64px_64px]" />
                    <div className="relative max-w-xl">
                      <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-full border border-white/10 bg-white/[0.04]">
                        <ShieldX className="h-6 w-6 text-primary" />
                      </div>
                      <p className="info-label text-primary/80">Google Maps gesperrt</p>
                      <h3 className="mt-3 font-display text-3xl uppercase leading-none text-foreground">
                        Karte erst nach Freigabe laden
                      </h3>
                      <p className="mt-4 max-w-lg text-sm leading-relaxed text-muted-foreground md:text-base">
                        Die eingebettete Karte bleibt blockiert, bis Sie externe Medien erlauben. So
                        wird vorab keine Verbindung zu Google hergestellt.
                      </p>

                      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                        <button
                          type="button"
                          onClick={() => savePreferences({ externalMedia: true })}
                          className="inline-flex items-center justify-center rounded-full border border-primary/30 bg-primary px-6 py-3 font-display text-sm uppercase tracking-[0.14em] text-primary-foreground transition-colors hover:bg-primary/90"
                        >
                          Karte freigeben
                        </button>
                        <button
                          type="button"
                          onClick={openPreferences}
                          className="inline-flex items-center justify-center rounded-full border border-white/12 bg-white/[0.04] px-6 py-3 font-display text-sm uppercase tracking-[0.14em] text-foreground transition-colors hover:bg-white/[0.08]"
                        >
                          Einstellungen
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default StandortSection;
